/**
 * Analyzer: Day Setup Spotlight
 *
 * Calls out the best + worst setup of the day when there's clear asymmetry
 * (best positive, worst negative). Silent when only one setup was traded
 * or when all setups performed similarly — the day grade handles the
 * summary in that case.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';

export const key = 'daySetupSpotlight';
export const tier = 'free';

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day || !Array.isArray(day.setups) || day.setups.length < 2) return null;

  const best = day.setups[0];
  const worst = day.setups[day.setups.length - 1];
  if (!best || !worst) return null;

  // Only spotlight when there's meaningful split — best positive, worst negative
  if (best.pnl > 0 && worst.pnl < 0) {
    const lossStr = Math.abs(worst.pnl).toFixed(0);
    return makeObservation({
      id: `${key}:${day.date}:split`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.INFO,
      category: CATEGORY.SETUP,
      title: `"${best.name}" carried the day; "${worst.name}" bled`,
      detail: `${best.name} +$${best.pnl.toFixed(0)} (${best.winRate.toFixed(0)}% WR). ${worst.name} −$${lossStr} (${worst.winRate.toFixed(0)}% WR).`,
      action: `Lean into "${best.name}" when conditions fit; re-check entry criteria for "${worst.name}".`,
      evidence: { best, worst, all: day.setups },
      metric: { label: 'Top Setup P&L', value: best.pnl.toFixed(0), unit: '$' },
      confidence: 0.75,
      priority: 65
    });
  }

  // All setups winning — celebrate
  if (worst.pnl > 0 && day.setups.length >= 2) {
    return makeObservation({
      id: `${key}:${day.date}:allwin`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.SETUP,
      title: 'All setups profitable today',
      detail: `${day.setups.length} setups, none in the red. Best: "${best.name}" +$${best.pnl.toFixed(0)}.`,
      action: 'Broad-edge days mean regime was with you. Log the market context so you recognize it again.',
      evidence: { all: day.setups },
      confidence: 0.7,
      priority: 45
    });
  }

  // All setups losing — warn
  if (best.pnl < 0) {
    return makeObservation({
      id: `${key}:${day.date}:allloss`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.WARNING,
      category: CATEGORY.SETUP,
      title: 'No setup worked today',
      detail: `Every setup finished negative. Best still lost $${Math.abs(best.pnl).toFixed(0)}.`,
      action: 'Consider regime mismatch — your setups may not fit current market behavior. Step back.',
      evidence: { all: day.setups },
      confidence: 0.8,
      priority: 78
    });
  }

  return null;
};
